import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { fadeInOutShrink } from '@app/animations/fade-in-out.animation';
import { AppConfig } from '@app/app.config';
import { LoadingProvider } from '@app/components/_shared/loading-spinner/loading-spinner.component';
import { ModalProvider } from '@app/components/_shared/modal/modal.component';
import { Breadcrumb } from '@app/models/_base/breadcrumb.model';
import { CourseArea } from '@app/models/courses/courseArea.model';
import { CourseService } from '@app/services/course.service';
import { Footer } from '@app/services/footer.service';
import { LocalisationService } from '@app/services/localisation.service';
import { QuestionnaireService } from '@app/services/questionnaire.service';
import { RouteBreadcrumbs } from '@app/services/routeBreadcrumbs.service';
import { UserService } from '@app/services/user.service';
import { Subscription } from 'rxjs';
import {
  BtnDropdownComponent,
  BtnDropdownItem
} from '@appShared/components/btn-dropdown/btn-dropdown.component';
import { DcdItem } from '@app/models/_shared/dcdItem';
import { SecondaryCourseFilterRequest } from '@app/models/courses/filterRequest.secondary.model';
import { DcdArea } from '@app/models/dcd/area.model';
import { InteractiveMapComponent } from '@app/components/_shared/interactive-map/interactive-map.component';
import { DcdArticle } from '@app/models/dcd/article.model';
import { Subject } from 'rxjs/Subject';

@Component({
  selector: 'app-course-area-list',
  templateUrl: './course-area-list.component.html',
  styleUrls: ['./course-area-list.component.scss'],
  animations: [fadeInOutShrink]
})
export class CourseAreaListComponent implements OnInit {
  public static route: string = 'course-areas';

  @ViewChild(BtnDropdownComponent)
  child;

  dropdownClickedSubject: Subject<any> = new Subject<any>();

  locale: any = {};

  dcdsLoaded: boolean = false;
  courseAreas: CourseArea[] = [];
  courseAreasLoaded: boolean = false;
  name: string = '';
  filterText: string = '';
  numberOfSelectedCourses: number = 0;
  maxCourseAreas: number = 5;
  userSubscription: Subscription;
  incomplete: boolean = false;
  public disableClick: boolean = false;
  disableHover: boolean;
  relatedDcds: DcdArea[] = [];
  loadedAreaArticles = [];

  constructor(
    private routeBreadcrumbs: RouteBreadcrumbs,
    private footerService: Footer,
    private userService: UserService,
    private courseService: CourseService,
    private questionnaireService: QuestionnaireService,
    private router: Router,
    private spinner: LoadingProvider,
    private modal: ModalProvider,
    private localisation: LocalisationService
  ) {}

  ngOnInit() {
    this.courseService.getRelatedDcds().subscribe(
      response => {
        this.relatedDcds = response.data;
        this.dcdsLoaded = true;
      },
      err => {}
    );
    this.locale = this.localisation.getTranslationsForComponent('CourseAreaListComponent');
    this.spinner.show(this.locale.loadingText, true);
    this.localisation.updateCentreComponent(false);

    this.disableHover = AppConfig.isTouchScreen;

    this.routeBreadcrumbs.breadcrumbs.next([new Breadcrumb(this.locale.breadcrumbText, null)]);

    this.footerService.update({
      title: this.locale.footerText,
      action: () => this.submitCourseArea(this)
    });

    this.userSubscription = this.userService.getUser().subscribe(user => {
      this.name = user.firstName;
    });

    this.loadCourseAreas();
  }

  clickedItem(data) {
    const clickedArticle = this.relatedDcds[data.parentId].articles.find(
      article => article.id === data.item.id
    );

    this.courseService.goToDcdArticle(clickedArticle.id);
  }

  clickedDropdown(data) {
    this.dropdownClickedSubject.next({ data });

    if (data.isOpen) {
      if (!this.loadedAreaArticles.includes(data.dropdownId)) {
        this.courseService.getAreaRelatedDcds(data.dropdownId).subscribe(response => {
          this.relatedDcds[data.dropdownId] = response.data;
          this.loadedAreaArticles.push(data.dropdownId);
        });
      }
    }
  }

  loadCourseAreas() {
    this.courseAreasLoaded = false;
    this.spinner.show(this.locale.loadingText, true);

    this.courseService
      .getCourseAreas()
      .finally(() => {
        this.spinner.hide();
      })
      .subscribe(
        response => {
          this.courseAreas = response.courseAreas;
          this.maxCourseAreas = response.maxCourseAreas;

          this.countNumberOfSelectedCourses();
          this.courseAreasLoaded = true;
        },
        error => {
          this.courseAreas = null;
          this.courseAreasLoaded = true;
        }
      );

    if (sessionStorage.getItem('areas-incomplete') == '1') {
      this.incomplete = true;
      sessionStorage.removeItem('areas-incomplete');
    }
  }

  setFormSubmitted(value) {
    this.disableClick = value;
  }

  submitCourseArea(scope: this): void {
    if (scope.numberOfSelectedCourses == 0) {
      scope.modal.create(this.locale.noneSelectedModalTitle, this.locale.noneSelectedModalMessage);
    } else if (scope.numberOfSelectedCourses > this.maxCourseAreas) {
      scope.modal.create(
        this.locale.maxSelectedModalTitle,
        (this.locale.maxSelectedModalMessage as string).format([this.maxCourseAreas.toString()])
      );
    } else {
      scope.router.navigate(['/centigrade/course-area-filters']);
    }
  }

  toggleCourse(course: CourseArea): void {
    if (!course.selected) {
      if (this.numberOfSelectedCourses + 1 > this.maxCourseAreas) {
        this.modal.create(
          this.locale.maxSelectedModalTitle,
          (this.locale.maxSelectedModalMessage as string).format([this.maxCourseAreas.toString()])
        );
      } else {
        this.setFormSubmitted(true);
        this.courseService
          .selectCourseArea(course.id)
          .finally(() => {
            this.setFormSubmitted(false);
            this.countNumberOfSelectedCourses();
          })
          .subscribe(
            result => {
              course.selected = true;
            },
            err => {}
          );
      }
    } else {
      this.setFormSubmitted(true);
      this.courseService
        .deselectCourseArea(course.id)
        .finally(() => {
          this.setFormSubmitted(false);
          this.countNumberOfSelectedCourses();
        })
        .subscribe(
          result => {
            course.selected = false;
          },
          err => {}
        );
    }

    this.courseService.updateCategorySelected(2);
    this.courseService.deleteSecondaryFilters().subscribe(filters => {});
  }

  countNumberOfSelectedCourses(): void {
    this.numberOfSelectedCourses = this.courseAreas.filter(
      courseArea => courseArea.selected
    ).length;

    if (this.numberOfSelectedCourses <= 0) {
      this.questionnaireService.statusOverride.next(100);
    } else {
      this.questionnaireService.statusOverride.next(null);
    }
  }

  viewCourseArea(id) {
    this.router.navigate(['/centigrade/course-areas/detail', id]);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/course-areas/list/course-area-list/course-area-list.component.ts